import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { isEqual, startCase } from 'lodash';
import {
	Fragment, useEffect, useState,
} from 'react';
import { motion } from 'framer-motion';
import {
	GenericLevels, useSuperGridValidate, SuperGridValidationEntity, focusSuperGridCell, VerticalSuperGridColDef, TransformedVerticalSuperGridColDef,
} from '@aleph/supergrid';
import { WarningSign, CornerBottomLeft } from '@aleph/shared-components';
import { GridApi } from 'ag-grid-community';

interface ValidationNavigatorProps<TLevel extends GenericLevels> {
	gridApi: GridApi | null
	isVertical?: boolean
	mappedFields?: Record<string, string>
	setLevel?: (level: TLevel) => void
	level?: TLevel
	initialHidden?: boolean
}

export const ValidationNavigator = <TLevel extends GenericLevels>({
	gridApi,
	isVertical = false,
	mappedFields = {},
	setLevel,
	level,
	initialHidden = false,
}: ValidationNavigatorProps<TLevel>) => {
	const { context: { validations } } = useSuperGridValidate({ gridApi });
	const [, setRender] = useState(0);
	const [hidden, setHidden] = useState(initialHidden);
	const [previewError, setPreviewError] = useState<SuperGridValidationEntity | undefined>();
	const [stagedFocus, setStagedFocus] = useState<SuperGridValidationEntity | undefined>();

	const updateRender = () => {
		setRender(render => render + 1);
	};

	useEffect(() => {
		setPreviewError(undefined);
	}, [hidden]);

	const focusErrorCell = (selectedRowIndex: number, verticalField: string, validation: SuperGridValidationEntity) => {
		setPreviewError(validation);

		if (!gridApi?.getDisplayedRowAtIndex(selectedRowIndex) || !gridApi?.getColumnDef(verticalField)) {
			return;
		}

		focusSuperGridCell({ api: gridApi, rowIndex: selectedRowIndex, field: verticalField });
	};

	const focusVerticalCell = (validation: SuperGridValidationEntity) => {
		const { id, field } = validation;

		const verticalField = (gridApi?.getColumnDefs() as Array<VerticalSuperGridColDef>)?.find(
			(colDef) => colDef.entityData?.hash === id || colDef.entityData?.id === id,
		)?.field ?? '';

		let selectedRowIndex = -1;

		gridApi?.forEachNode(node => {
			const nodeColId = node.data?.colId ?? node.data?.field ?? '';

			if ((mappedFields[nodeColId] ?? nodeColId) === field) {
				selectedRowIndex = node.rowIndex ?? -1;
			}
		});

		focusErrorCell(selectedRowIndex, verticalField, validation);
	};

	useEffect(() => {
		if (stagedFocus) {
			focusVerticalCell(stagedFocus);
			setStagedFocus(undefined);
		}

		gridApi?.addEventListener('cellFocused', updateRender);

		return () => {
			gridApi?.removeEventListener('cellFocused', updateRender);
		};
	}, [gridApi]);

	const navigateToValidation = (validation: SuperGridValidationEntity) => () => {
		const { id, field } = validation;
		let selectedRowIndex = -1;

		gridApi?.forEachNode(node => {
			if ((node.data?.hash === id || node.data?.id === id) && selectedRowIndex === -1) {
				selectedRowIndex = node.rowIndex ?? -1;
			}
		});

		focusErrorCell(selectedRowIndex, field, validation);
	};

	const navigateToVerticalValidation = (validation: SuperGridValidationEntity) => () => {
		if (level === validation.level) {
			focusVerticalCell(validation);

			return;
		}

		setLevel?.(validation.level as TLevel);
		setStagedFocus(validation);
	};

	const isValidationFocused = (validation: SuperGridValidationEntity) => {
		const focusedCell = gridApi?.getFocusedCell();

		if (isVertical) {
			const focusedRow = gridApi?.getDisplayedRowAtIndex(focusedCell?.rowIndex ?? -1);
			const isColSelected = (focusedCell?.column?.getColDef() as TransformedVerticalSuperGridColDef<TLevel>)
				?.entityData?.hash === validation.id;
			const isRowSelected = focusedRow?.data?.field === validation.field || focusedRow?.data?.colId === validation.field;

			return isColSelected && isRowSelected;
		}

		const selectedRowData = gridApi?.getDisplayedRowAtIndex(focusedCell?.rowIndex ?? -1)?.data;
		const isRowSelected = [selectedRowData?.hash, selectedRowData?.id].includes(validation?.id);

		const colDef = focusedCell?.column?.getColDef();

		return (colDef?.field === validation.field || colDef?.colId === validation.field) && isRowSelected;
	};

	if (validations.length === 0) {
		return null;
	}

	// @ts-ignore
	return createPortal(
		<div
			className={classNames('absolute bg-white flex flex-col left-[32px] bottom-[32px] shadow-around-sm px-[12px] max-w-[280px] max-h-[480px] overflow-y-auto', {
				'rounded-2xl': !hidden,
				'rounded-lg':  hidden,
			})}
			data-testid="validation-box-component"
		>
			<div className={classNames('flex h-[40px] items-center justify-between gap-[12px]', {
				'pt-[8px]': !hidden,
			})}
			>
				<div className="flex h-[32px] gap-[8px] text-[12px] py-[8px] text-[12px]">
					<div className="flex h-[16px] gap-[10px] text-red-500">
						<WarningSign
							height={16}
						/>
						<strong>ERRORS</strong>
					</div>
				</div>
				<div
					className={classNames('cursor-pointer border border-transparent hover:border-gray-300 rounded active:bg-gray-200 p-[4px]', {
						'rotate-180': hidden,
					})}
					onClick={() => { setHidden(curr => !curr); }}
				>
					<CornerBottomLeft
						height={16}
						width={16}
					/>
				</div>
			</div>

			<motion.div
				className="overflow-hidden"
				animate={{
					height: hidden ? 0 : 'auto',
					width:  hidden ? 0 : 'auto',
				}}
				initial={{
					height: hidden ? 0 : 'auto',
					width:  hidden ? 0 : 'auto',
				}}
			>
				{
					validations.sort(
						(v1, v2) => v1.field?.localeCompare(v2.field ?? ''),
					).map((validation, idx) => (
						<Fragment
							key={`${validation.id}_${validation.field}`}
						>
							<button
								key={`${validation.id}_${validation.field}`}
								onClick={
									isEqual(previewError, validation)
										? () => setPreviewError(undefined)
										: (
											isVertical
												? navigateToVerticalValidation(validation)
												: navigateToValidation(validation)
										)
								}
								className={classNames('h-[32px] w-full gap-[8px] text-left text-[12px] font-bold flex items-center justify-between whitespace-nowrap overflow-hidden', {
									'text-red-500':                   isValidationFocused(validation),
									'border-b-[1px] border-gray-300': validations.length - 1 !== idx,
								})}
								data-testid={`validation-${idx}`}
							>
								<div className='max-w-[85%] overflow-ellipsis overflow-hidden'>
									{startCase(validation.field)}
								</div>
								<div className="text-gray-700 font-normal">
									{startCase(validation.level?.toLowerCase?.())}
								</div>
							</button>
							{
								previewError
								&& previewError.field === validation.field
								&& previewError.id === validation.id
								&& previewError.level === validation.level
								&& <div className="py-[8px] flex flex-col gap-[8px]">
									<div className="text-[12px]">{validation.message}</div>
								</div>
							}
						</Fragment>
					))
				}
			</motion.div>
		</div>,
		document.body,
	);
};
